import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectHasSelectedCompanyFilterAccess } from 'containers/App/selectors'
import {
  DATE_FILTER_CHANGE as HOME_DATE_FILTER_CHANGE,
  SCANBACK_CHANGE,
} from 'containers/Dashboard/Home/constants'
import {
  selectDateRange as selectHomeDateRange,
  selectWithoutScanback,
} from 'containers/Dashboard/Home/selectors'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../../PartnerOptions/constants'
import { selectCompanyFilter } from '../../../PartnerOptions/selectors'
import { shareResetError } from '../../../Share/actions'
import { LIST_TYPE } from '../constants'
import { selectListType } from '../List/selectors'

import {
  fetchAllFiltersError,
  fetchAllFiltersSuccess,
  fetchDataError,
  fetchDataSuccess,
  fetchMailboxAnomalyError,
  fetchMailboxAnomalySuccess,
  fetchMitigationDataError,
  fetchMitigationDataSuccess,
  fetchMostTargetedEmployeesError,
  fetchMostTargetedEmployeesSuccess,
  fetchTopProfilesError,
  fetchTopProfilesSuccess,
} from './actions'
import {
  DATA_REQUEST_ASYNC,
  DATE_FILTER_CHANGE,
  FETCH_ALL_FILTERS_ASYNC,
  MOST_ATTACKED_EMPLOYEES_REQUEST_ASYNC,
} from './constants'
import { selectDateFilter } from './selectors'

const fetchDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      DATA_REQUEST_ASYNC.INITIATE,
      DATE_FILTER_CHANGE,
      SCANBACK_CHANGE
    ),
    filter((action) => {
      if (
        action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS ||
        action.type === SCANBACK_CHANGE
      )
        return matchURL(URL_CONST.dashboard.mitigation)
      return true
    }),
    mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          state$.value.partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(state$.value)),
          selectWithoutScanback(state$.value),
          { mitigation: false, mailboxAnomaly: false, topProfiles: false }
        )
        .pipe(
          mergeMap((response) => of(fetchDataSuccess(response.response), shareResetError())),
          catchError((error) => of(epicsErrorHandler(fetchDataError, error))),
          takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const fetchTopProfilesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DATA_REQUEST_ASYNC.SUCCESS, SCANBACK_CHANGE),
    filter((action) => {
      if (action.type === SCANBACK_CHANGE) return matchURL(URL_CONST.dashboard.mitigation)
      return true
    }),
    mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          state$.value.partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(state$.value)),
          selectWithoutScanback(state$.value),
          { mitigation: false, mailboxAnomaly: false, topProfiles: true }
        )
        .pipe(
          mergeMap((response) => of(fetchTopProfilesSuccess(response.response), shareResetError())),
          catchError((error) => of(epicsErrorHandler(fetchTopProfilesError, error)))
        )
    )
  )

const fetchMostTargetedEmployeesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      MOST_ATTACKED_EMPLOYEES_REQUEST_ASYNC.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      DATE_FILTER_CHANGE,
      HOME_DATE_FILTER_CHANGE,
      SCANBACK_CHANGE
    ),
    filter((action) => {
      if (
        action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS ||
        action.type === HOME_DATE_FILTER_CHANGE ||
        action.type === SCANBACK_CHANGE
      )
        return matchURL(URL_CONST.dashboard.home) || matchURL(URL_CONST.dashboard.mitigation)
      return true
    }),
    // companyFilter 403s unless the user can swap into the selected company.
    // A loaded brand child can still be rejected. Cancel resets the loader;
    // FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS re-fires this.
    mergeMap(() => {
      const state = state$.value
      if (!selectHasSelectedCompanyFilterAccess(state))
        return of(MOST_ATTACKED_EMPLOYEES_REQUEST_ASYNC.cancel())
      const emailDateRange = matchURL(URL_CONST.dashboard.home)
        ? selectHomeDateRange(state)
        : getRangeFromDateFilter(selectDateFilter(state))

      return api.dashboard
        .getMostTargetedEmployees(
          selectCompanyFilter(state)?.value,
          emailDateRange,
          selectWithoutScanback(state)
        )
        .pipe(
          mergeMap((response) =>
            of(fetchMostTargetedEmployeesSuccess(response?.response), shareResetError())
          ),
          catchError((error) => of(epicsErrorHandler(fetchMostTargetedEmployeesError, error))),
          takeUntil(action$.pipe(ofType(MOST_ATTACKED_EMPLOYEES_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const fetchMitigationDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DATA_REQUEST_ASYNC.SUCCESS, SCANBACK_CHANGE),
    filter((action) => {
      if (action.type === SCANBACK_CHANGE) return matchURL(URL_CONST.dashboard.mitigation)
      return true
    }),
    mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          state$.value.partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(state$.value)),
          selectWithoutScanback(state$.value),
          { mitigation: true, mailboxAnomaly: false, topProfiles: false }
        )
        .pipe(
          mergeMap((response) =>
            of(fetchMitigationDataSuccess(response.response), shareResetError())
          ),
          catchError((error) => of(epicsErrorHandler(fetchMitigationDataError, error)))
        )
    )
  )

const fetchMailboxAnomalyEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DATA_REQUEST_ASYNC.SUCCESS, SCANBACK_CHANGE),
    filter((action) => {
      if (action.type === SCANBACK_CHANGE) return matchURL(URL_CONST.dashboard.mitigation)
      return true
    }),
    mergeMap(() =>
      api.dashboard
        .getThreatProtection(
          state$.value.partnerOptions.selectedCompany?.value,
          getRangeFromDateFilter(selectDateFilter(state$.value)),
          selectWithoutScanback(state$.value),
          { mitigation: false, mailboxAnomaly: true, topProfiles: false }
        )
        .pipe(
          mergeMap((response) =>
            of(fetchMailboxAnomalySuccess(response.response), shareResetError())
          ),
          catchError((error) => of(epicsErrorHandler(fetchMailboxAnomalyError, error)))
        )
    )
  )

const fetchAllFiltersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS, FETCH_ALL_FILTERS_ASYNC.INITIATE),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.mitigation)
      return true
    }),
    mergeMap(() => {
      const isBanners = selectListType(state$.value) === LIST_TYPE.BANNERS
      return (
        isBanners
          ? api.dashboard.getThreatProtectionBannersFilters(
              state$.value.partnerOptions.selectedCompany?.value
            )
          : api.dashboard.getThreatProtectionIncidentsFilters(
              state$.value.partnerOptions.selectedCompany?.value
            )
      ).pipe(
        map((response) => fetchAllFiltersSuccess(isBanners ? response : response.response)),
        catchError((error) => of(epicsErrorHandler(fetchAllFiltersError, error)))
      )
    })
  )

export default combineEpics(
  fetchDataEpic,
  fetchTopProfilesEpic,
  fetchMitigationDataEpic,
  fetchMailboxAnomalyEpic,
  fetchAllFiltersEpic,
  fetchMostTargetedEmployeesEpic
)
